/**
 * This is a modified version of `procedureDefMutator` from @blockly/block-shareable-procedures.
 * We removed compose() and decompose() methods. These methods automatically
 * add a gear icon UI that we do not want. A future version of the plugin will
 * export this mutator (and other extensions), but this will require bumping to
 * Blockly v10. We also updated the mutation and extra state methods to save the function's
 * description.
 * TODO: Once we are on Blockly v10, we can simplify this by importing `procedureDefMutator`
 * from @blockly/block-shareable-procedures and calling the duplicated methods inside our versions of them.
 * This will allow us to get rid of duplicated code.
 */

import {
  ObservableParameterModel,
  isProcedureBlock,
} from '@blockly/block-shareable-procedures';
import * as GoogleBlockly from 'blockly/core';

import {ProcedureBlock} from '@cdo/apps/blockly/types';
import {FALSEY_DEFAULT, readBooleanAttribute} from '@cdo/apps/blockly/utils';

import {
  getBlockDescription,
  setBlockDescription,
} from './functionMutatorHelpers';

export const procedureDefMutator = {
  hasStatements_: true,

  /**
   * Create XML to represent the argument inputs.
   * Backwards compatible serialization implementation.
   * @returns XML storage element.
   * @this {Blockly.Block}
   */
  mutationToDom: function (this: ProcedureBlock) {
    const container = Blockly.utils.xml.createElement('mutation');
    const params =
      this.getProcedureModel().getParameters() as ObservableParameterModel[];
    for (let i = 0; i < params.length; i++) {
      const parameter = Blockly.utils.xml.createElement('arg');
      const varModel = params[i].getVariableModel();
      parameter.setAttribute('name', varModel.name);
      parameter.setAttribute('varid', varModel.getId());
      container.appendChild(parameter);
    }

    // Save whether the statement input is visible.
    if (!this.hasStatements_) {
      container.setAttribute('statements', 'false');
    }
    if (this.invisible) {
      container.setAttribute('invisible', 'true');
    }
    return container;
  },

  /**
   * Parse XML to restore the argument inputs.
   * Backwards compatible serialization implementation.
   * @param xmlElement XML storage element.
   * @this {Blockly.Block}
   */
  domToMutation: function (this: ProcedureBlock, xmlElement: Element) {
    for (let i = 0; i < xmlElement.childNodes.length; i++) {
      const node = xmlElement.childNodes[i];
      const nodeName = node.nodeName.toLowerCase();
      if (nodeName === 'arg') {
        const varId = (node as Element).getAttribute('varid');
        this.getProcedureModel().insertParameter(
          new ObservableParameterModel(
            this.workspace,
            (node as Element).getAttribute('name') || '',
            undefined,
            varId || ''
          ),
          i
        );
      } else if (nodeName === 'description') {
        // CDO Blockly projects stored descriptions in a separate tag within the mutation.
        this.description = node.textContent;
      }
    }

    this.userCreated = readBooleanAttribute(
      xmlElement,
      'userCreated',
      FALSEY_DEFAULT
    );
    this.invisible = readBooleanAttribute(
      xmlElement,
      'invisible',
      FALSEY_DEFAULT
    );
    this.setStatements_(xmlElement.getAttribute('statements') !== 'false');
    if (!this.description) {
      // Google Blockly projects store descriptions in a separate field.
      setBlockDescription(this, this.getFieldValue('DESCRIPTION'));
    }
  },

  /**
   * Returns the state of this block as a JSON serializable object.
   *
   * @param doFullSerialization  Tells the block if it should serialize
   *     its entire state (including data stored in the backing procedure
   *     model). Used for copy-paste.
   * @returns The state of this block, e.g. the parameters and statements.
   */
  saveExtraState: function (
    this: ProcedureBlock,
    doFullSerialization: boolean
  ) {
    const state = Object.create(null);
    state['description'] = getBlockDescription(this);
    state['procedureId'] = this.getProcedureModel().getId();
    state['initialDeleteConfig'] = this.isOwnDeletable();
    state['initialEditConfig'] = this.isOwnEditable();
    state['initialMoveConfig'] = this.isOwnMovable();
    state['userCreated'] = this.userCreated;
    state['invisible'] = this.invisible;

    if (doFullSerialization) {
      // If fullSerialization is not true, the system will reuse an existing procedure model by ID.
      // This is necessary for the modal function editor.
      // If fullSerialization is true, it will instead use the model created at block instantiation.
      // This is necessary for single-workspace labs so that multiple functions do not share the same model.
      state['fullSerialization'] = !Blockly.useModalFunctionEditor;
      const params =
        this.getProcedureModel().getParameters() as ObservableParameterModel[];

      if (params.length) {
        state['params'] = params.map(p => {
          return {
            name: p.getName(),
            id: p.getVariableModel().getId(),
            // Ideally this would be id, and the other would be varId,
            // but backwards compatibility :/
            paramId: p.getId(),
          };
        });
      }
    }
    if (!this.hasStatements_) {
      state['hasStatements'] = false;
    }
    return state;
  },

  /**
   * Accepts a JSON serializable state value and applies it to the block.
   * Overridden to support initial block states for the modal function editor,
   * and legacy state for user-created functions and invisible blocks.
   * @param state The state to apply to this block (see saveExtraState above).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadExtraState: function (this: ProcedureBlock, state: Record<string, any>) {
    const map = this.workspace.getProcedureMap();
    const procedureId = state['procedureId'];
    if (map.has(procedureId) && !state['fullSerialization']) {
      if (map.has(this.model_.getId())) {
        map.delete(this.model_.getId());
      }
      this.model_ = map.get(procedureId)!;
    }

    const model = this.getProcedureModel();
    const newParams: {name: string; id: string}[] = state['params'] ?? [];
    const newIds = new Set(newParams.map(p => p.id));
    const currParams = model.getParameters();
    if (state['fullSerialization']) {
      for (let i = currParams.length - 1; i >= 0; i--) {
        if (!newIds.has(currParams[i].getId())) {
          model.deleteParameter(i);
        }
      }
    }
    for (let i = 0; i < newParams.length; i++) {
      const {name, id, paramId} = state['params'][i];
      this.getProcedureModel().insertParameter(
        new ObservableParameterModel(this.workspace, name, paramId, id),
        i
      );
    }

    // Customization: Sets the description field of the block.
    setBlockDescription(this, state['description']);

    this.doProcedureUpdate();

    // Customization: Sets the initial delete/edit/move configuration of the block.
    if (!Blockly.useModalFunctionEditor) {
      this.setDeletable(state['initialDeleteConfig'] === false ? false : true);
      this.setEditable(state['initialEditConfig'] === false ? false : true);
      this.setMovable(state['initialMoveConfig'] === false ? false : true);
    }

    this.setStatements_(state['hasStatements'] === false ? false : true);

    // Customization: Handles the legacy state for user-created functions and invisible blocks.
    this.userCreated = state['userCreated'];
    this.invisible = state['invisible'];
  },

  /**
   * Returns true if there is no definition block currently associated with the
   * given procedure ID. False otherwise.
   * @param procedureId The ID of the procedure to check for a claiming
   *     block.
   * @returns True if there is no definition block currently associated
   *     with the given procedure ID. False otherwise.
   */
  noBlockHasClaimedModel_(this: ProcedureBlock, procedureId: string) {
    const model = this.workspace.getProcedureMap().get(procedureId);
    return this.workspace
      .getAllBlocks(false)
      .every(
        b =>
          !isProcedureBlock(b) ||
          !b.isProcedureDef() ||
          b.getProcedureModel() !== model
      );
  },

  /**
   * Deletes any parameters from the procedure model that do not have associated
   * parameter blocks in the mutator.
   * @param containerBlock Root block in the mutator.
   */
  deleteParamsFromModel_: function (
    this: ProcedureBlock,
    containerBlock: GoogleBlockly.Block
  ) {
    const ids = new Set(
      containerBlock.getDescendants(/*ordered*/ false).map(b => b.id)
    );
    const model = this.getProcedureModel();
    const count = model.getParameters().length;
    for (let i = count - 1; i >= 0; i--) {
      if (!ids.has(model.getParameter(i).getId())) {
        model.deleteParameter(i);
      }
    }
  },

  /**
   * Renames any parameters in the procedure model whose associated parameter
   * blocks have been renamed.
   * @param containerBlock Root block in the mutator.
   */
  renameParamsInModel_: function (
    this: ProcedureBlock,
    containerBlock: GoogleBlockly.Block
  ) {
    const model = this.getProcedureModel();

    let i = 0;
    let paramBlock = containerBlock.getInputTargetBlock('STACK');
    while (paramBlock && !paramBlock.isInsertionMarker()) {
      const param = model.getParameter(i);
      if (
        param &&
        param.getId() === paramBlock.id &&
        param.getName() !== paramBlock.getFieldValue('NAME')
      ) {
        param.setName(paramBlock.getFieldValue('NAME'));
      }
      paramBlock =
        paramBlock.nextConnection && paramBlock.nextConnection.targetBlock();
      i++;
    }
  },

  /**
   * Adds new parameters to the procedure model for any new procedure parameter
   * blocks.
   * @param containerBlock Root block in the mutator.
   */
  addParamsToModel_: function (
    this: ProcedureBlock,
    containerBlock: GoogleBlockly.Block
  ) {
    const model = this.getProcedureModel();

    let i = 0;
    let paramBlock = containerBlock.getInputTargetBlock('STACK');
    while (paramBlock && !paramBlock.isInsertionMarker()) {
      if (
        !model.getParameter(i) ||
        model.getParameter(i).getId() !== paramBlock.id
      ) {
        model.insertParameter(
          new ObservableParameterModel(
            this.workspace,
            paramBlock.getFieldValue('NAME'),
            paramBlock.id
          ),
          i
        );
      }
      paramBlock =
        paramBlock.nextConnection && paramBlock.nextConnection.targetBlock();
      i++;
    }
  },
};
